import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAdminCounts } from '../context/AdminCountsContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import {
  Home,
  Calendar,
  Building,
  DollarSign,
  Users,
  BarChart3,
  LogOut,
  ChevronLeft,
  Menu,
  CalendarDays,
  UserCircle,
  Heart,
  ClipboardList
} from 'lucide-react';
import { useState } from 'react';
import logo from '../assets/logo.png';

export default function AppLayout() {
  const { user, isHost, isAdmin, logout } = useAuth();
  const { pendingReviewCount } = useAdminCounts();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const NavItem = ({ to, icon: Icon, children, badge }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
          isActive
            ? 'bg-primary text-white'
            : 'text-slate-600 hover:bg-slate-100'
        }`
      }
      onClick={() => setSidebarOpen(false)}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium flex-1">{children}</span>
      {badge > 0 && (
        <Badge 
          className="bg-amber-500 hover:bg-amber-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center"
          data-testid="pending-review-badge"
        >
          {badge}
        </Badge>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white border-r border-slate-200
          transform transition-transform lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-slate-200">
            <NavLink to="/" className="flex items-center gap-2">
              <img src={logo} alt="Southwest FL Vacation Rentals" className="h-14 w-auto" />
            </NavLink>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 p-4">
            <nav className="space-y-1">
              {/* Guest Section */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-4">
                  Guest
                </p>
                <NavItem to="/app" icon={Home}>Dashboard</NavItem>
                <NavItem to="/app/bookings" icon={Calendar}>My Bookings</NavItem>
                <NavItem to="/app/saved" icon={Heart}>Saved</NavItem>
              </div>

              {/* Host Section */}
              {isHost && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-4">
                    Host
                  </p>
                  <NavItem to="/app/host" icon={BarChart3}>Host Dashboard</NavItem>
                  <NavItem to="/app/host/properties" icon={Building}>My Properties</NavItem>
                  <NavItem to="/app/host/calendar" icon={CalendarDays}>Calendar</NavItem>
                  <NavItem to="/app/host/reservations" icon={Calendar}>Reservations</NavItem>
                  <NavItem to="/app/host/payouts" icon={DollarSign}>Payouts</NavItem>
                </div>
              )}

              {/* Admin Section */}
              {isAdmin && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-4">
                    Admin
                  </p>
                  <NavItem to="/app/admin" icon={BarChart3}>Dashboard</NavItem>
                  <NavItem to="/app/admin/hosts" icon={Users}>Hosts</NavItem>
                  <NavItem to="/app/admin/users" icon={Users}>All Users</NavItem>
                  <NavItem 
                    to="/app/admin/properties" 
                    icon={Building} 
                    badge={pendingReviewCount}
                  >
                    Properties
                  </NavItem>
                  <NavItem to="/app/admin/bookings" icon={Calendar}>Bookings</NavItem>
                  <NavItem to="/app/admin/audit-log" icon={ClipboardList}>Audit Log</NavItem>
                </div>
              )}
              
              {/* Account Section */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-4">
                  Account
                </p>
                <NavItem to="/app/profile" icon={UserCircle}>Profile Settings</NavItem>
              </div>
            </nav>
          </ScrollArea>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={handleLogout}
              data-testid="sidebar-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-4 py-3 lg:px-6 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>
          
          <NavLink 
            to="/" 
            className="text-sm text-slate-600 hover:text-primary flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Site
          </NavLink>
        </header>

        {/* Email Verification Banner */}
        <EmailVerificationBanner />

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
